<template>
  <div class="container mt-5" style="max-width: 460px;">
    <div class="card shadow-sm">
      <div class="card-body p-4">
        <h4 class="card-title text-center mb-4 fw-bold">정보 수정</h4>

        <div v-if="loading" class="text-center py-5 text-muted">불러오는 중...</div>

        <div v-else>
          <!-- 아이디 (수정 불가) -->
          <div class="mb-3">
            <label class="form-label">아이디</label>
            <input type="text" class="form-control" :value="form.id" disabled style="background-color: #f5f5f5;" />
            <small class="text-muted">아이디는 변경할 수 없습니다.</small>
          </div>

          <!-- 이름 -->
          <div class="mb-3">
            <label class="form-label">이름 <span class="text-muted small">(최대 50자)</span></label>
            <input v-model="form.name" type="text" class="form-control" maxlength="50" placeholder="이름 입력" />
          </div>

          <!-- 차량번호 -->
          <div class="mb-3">
            <label class="form-label">차량번호 <span class="text-muted small"></span></label>
            <input v-model="form.carNumber" type="text" class="form-control" maxlength="20" placeholder="예: 12가3456 또는 서울12가3456" />
            <div v-if="carNumberError" class="form-text text-danger mt-1">{{ carNumberError }}</div>
          </div>

          <!-- 전화번호 -->
          <div class="mb-3">
            <label class="form-label">전화번호 <span class="text-muted small"></span></label>

            <div v-if="!isTelEditing" class="input-group">
              <input
                :value="originalTel || ''"
                type="text"
                class="form-control"
                disabled
                style="background-color: #f8f9fa;"
              />
              <button class="btn btn-outline-secondary" type="button" @click="startPhoneEdit">
                전화번호 수정
              </button>
            </div>

            <div v-else class="input-group">
              <input 
                v-model="form.tel" 
                type="text" 
                class="form-control" 
                maxlength="20" 
                placeholder="예: 010-1234-5678"
                @input="(e) => form.tel = formatPhoneDisplay(e.target.value)"
              />
              <button 
                class="btn btn-outline-secondary" 
                type="button" 
                @click="requestAuthCode"
                :disabled="authSending || !form.tel || form.tel.replace(/\D/g, '').length < 10"
              >
                {{ authSending ? '전송 중...' : '인증' }}
              </button>
            </div>
            <div v-if="authError" class="form-text text-danger mt-1">{{ authError }}</div>
          </div>

          <!-- 인증번호 입력 섹션 -->
          <div v-if="showAuthInput && authSent" class="mb-3">
            <label class="form-label">인증번호 입력</label>
            <div class="input-group mb-2">
              <input 
                v-model="inputAuthCode" 
                type="text" 
                class="form-control" 
                maxlength="6" 
                placeholder="6자리 인증번호"
                @keyup.enter="verifyAuthCode"
              />
              <button 
                class="btn btn-primary" 
                type="button" 
                @click="verifyAuthCode"
              >
                인증 확인
              </button>
            </div>
            <div class="d-flex justify-content-between align-items-center">
              <small class="text-muted">
                <span v-if="authTimeRemaining > 0" class="text-danger fw-bold">
                  제한시간: {{ Math.floor(authTimeRemaining / 60) }}:{{ String(authTimeRemaining % 60).padStart(2, '0') }}
                </span>
                <span v-else class="text-danger">시간 초과</span>
              </small>
              <small v-if="resendRemaining > 0" class="text-muted">재전송 대기: {{ resendRemaining }}초</small>
              <button 
                v-else-if="authTimeRemaining > 0 && !authSending"
                class="btn btn-sm btn-link p-0" 
                @click="requestAuthCode"
              >
                재전송
              </button>
            </div>
          </div>

          <!-- 비밀번호 (선택사항) -->
          <div class="mb-3">
            <label class="form-label">
              새 비밀번호 <span class="text-muted small">(변경 시만 입력 / 최소 4자)</span>
            </label>
            <input v-model="form.password" type="password" class="form-control" placeholder="새 비밀번호 입력 (선택사항)" />
            <div v-if="form.password && form.password.length < 4" class="form-text text-danger">비밀번호는 최소 4자 이상이어야 합니다.</div>
          </div>

          <!-- 에러/성공 메시지 -->
          <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>
          <div v-if="success" class="alert alert-success py-2">{{ success }}</div>

          <!-- 버튼 -->
          <button 
            class="btn btn-dark w-100 mb-2" 
            :disabled="loading"
            @click="saveChanges"
          >
            {{ loading ? '처리 중...' : '저장' }}
          </button>
          <button class="btn btn-outline-secondary w-100" @click="$router.push('/')">
            돌아가기
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'UserInfoEditView',
  data() {
    return {
      form: { id: '', name: '', tel: '', carNumber: '', password: '' },
      loading: false,
      error: null,
      success: null,
      carNumberError: '',
      // 전화번호 인증 관련
      telVerified: false,
      authSending: false,
      showAuthInput: false,
      authSent: false,
      generatedAuthCode: '',
      inputAuthCode: '',
      authError: '',
      authStartTime: null,
      authTimeRemaining: 0,
      authTimer: null,
      resendRemaining: 0,
      resendTimer: null,
      originalTel: '',
      originalCarNumber: '',
      isTelEditing: false
    }
  },
  mounted() {
    this.fetchUserInfo()
  },
  methods: {
    async fetchUserInfo() {
      this.loading = true
      try {
        const userStr = sessionStorage.getItem('user')
        if (!userStr) {
          this.error = '로그인이 필요합니다.'
          this.$router.push('/login')
          return
        }
        
        const user = JSON.parse(userStr)
        const userSeq = user.UserSeq
        
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/user/${userSeq}`)
        this.form = {
          id: res.data.UserID,
          name: res.data.Name,
          tel: res.data.Tel || '',
          carNumber: res.data.CarNumber,
          password: ''
        }
        this.originalTel = res.data.Tel || ''
        this.originalCarNumber = res.data.CarNumber
        this.isTelEditing = false
        this.telVerified = false
      } catch (e) {
        this.error = e.response?.data?.message || '정보를 불러오지 못했습니다.'
      } finally {
        this.loading = false
      }
    },
    startPhoneEdit() {
      this.isTelEditing = true
      this.form.tel = ''
      this.telVerified = false
      this.authError = ''
      this.showAuthInput = false
      this.authSent = false
      this.inputAuthCode = ''
    },
    formatPhoneDisplay(v) {
      if (!v) return ''
      const d = v.replace(/\D/g, '')
      if (d.length === 11) return d.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
      if (d.length === 10) return d.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
      return v
    },
    getPhoneNumberDigitsOnly() {
      return this.form.tel.replace(/\D/g, '')
    },
    async requestAuthCode() {
      if (this.authSending) return
      this.authError = ''
      
      const phoneDigits = this.getPhoneNumberDigitsOnly()
      console.log('전화번호 숫자만:', phoneDigits)
      
      if (!phoneDigits || phoneDigits.length < 10) {
        this.authError = '휴대폰 번호를 정확히 입력하세요.'
        console.log('에러: 휴대폰 번호 형식 오류')
        return
      }
      
      // 입력한 번호가 원래 번호와 같은지 확인
      const formattedNewTel = this.formatPhoneDisplay(this.form.tel)
      console.log('포맷된 새 번호:', formattedNewTel, '원래 번호:', this.originalTel)
      
      if (formattedNewTel === this.originalTel) {
        console.log('번호가 같음 - 인증 스킵')
        this.telVerified = true
        this.showAuthInput = false
        this.authSent = false
        return
      }
      
      console.log('새 번호로 인증 시작')
      // 6자리 인증번호 생성
      this.generatedAuthCode = Math.floor(100000 + Math.random() * 900000).toString()
      console.log('생성된 인증번호:', this.generatedAuthCode)
      this.authSending = true
      
      try {
        const msgContent = `[은평구시설관리공단] 인증번호: ${this.generatedAuthCode}`
        console.log('전송할 메시지:', msgContent)
        
        const res = await fetch(`${process.env.VUE_APP_API_BASE}/api/ebpp/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phoneNumber: phoneDigits,
            msg: msgContent
          })
        })
        
        const result = await res.json()
        console.log('API 응답:', result)
        
        if (result.success) {
          console.log('인증번호 전송 성공')
          this.showAuthInput = true
          this.authSent = true
          this.inputAuthCode = ''
          this.authError = ''
          
          // 3분(180초) 타이머 시작
          this.authStartTime = Date.now()
          this.authTimeRemaining = 180
          
          // 재전송 대기 60초
          this.resendRemaining = 60
          
          if (this.authTimer) clearInterval(this.authTimer)
          if (this.resendTimer) clearInterval(this.resendTimer)
          
          // 인증 시간 타이머
          this.authTimer = setInterval(() => {
            this.authTimeRemaining--
            if (this.authTimeRemaining <= 0) {
              clearInterval(this.authTimer)
              this.authTimer = null
            }
          }, 1000)
          
          // 재전송 대기 타이머
          this.resendTimer = setInterval(() => {
            this.resendRemaining--
            if (this.resendRemaining <= 0) {
              clearInterval(this.resendTimer)
              this.resendTimer = null
            }
          }, 1000)
        } else {
          console.log('인증번호 전송 실패:', result.message)
          this.authError = '인증번호 전송 실패: ' + (result.message || '알 수 없는 오류')
        }
      } catch (e) {
        console.error('인증번호 전송 중 오류:', e)
        this.authError = '인증번호 전송 중 오류가 발생했습니다: ' + (e.message || '')
      } finally {
        this.authSending = false
      }
    },
    
    verifyAuthCode() {
      this.authError = ''
      
      if (this.authTimeRemaining <= 0) {
        this.authError = '인증 제한시간이 초과되었습니다. 다시 요청해주세요.'
        return
      }
      
      if (this.inputAuthCode === this.generatedAuthCode) {
        this.telVerified = true
        this.showAuthInput = false
        this.authSent = false
        
        if (this.authTimer) {
          clearInterval(this.authTimer)
          this.authTimer = null
        }
        if (this.resendTimer) {
          clearInterval(this.resendTimer)
          this.resendTimer = null
        }
      } else {
        this.authError = '인증번호가 일치하지 않습니다.'
      }
    },

    async saveChanges() {
      this.error = null
      this.success = null
      this.carNumberError = ''
      
      // 전화번호 수정 여부 확인
      const telInput = this.isTelEditing ? this.form.tel.trim() : ''
      const finalTel = telInput ? this.formatPhoneDisplay(telInput) : this.originalTel
      
      // 수정 모드에서 새 번호 입력 시 인증 필수
      if (this.isTelEditing && telInput && !this.telVerified) {
        this.error = '새 전화번호 인증이 필요합니다.'
        return
      }
      
      if (!this.form.name.trim()) {
        this.error = '이름을 입력하세요.'
        return
      }
      if (!finalTel.trim()) {
        this.error = '전화번호를 입력하세요.'
        return
      }
      if (!this.form.carNumber.trim()) {
        this.error = '차량번호를 입력하세요.'
        return
      }
      if (this.form.password && this.form.password.length < 4) {
        this.error = '비밀번호는 최소 4자 이상이어야 합니다.'
        return
      }
      
      this.loading = true
      try {
        const userStr = sessionStorage.getItem('user')
        const user = JSON.parse(userStr)
        const userSeq = user.UserSeq
        
        await axios.put(`${process.env.VUE_APP_API_BASE}/api/user/${userSeq}`, {
          name: this.form.name,
          tel: finalTel,
          carNumber: this.form.carNumber,
          password: this.form.password || null
        })
        this.success = '정보가 수정되었습니다. 홈으로 이동합니다.'
        setTimeout(() => this.$router.push('/'), 1500)
      } catch (e) {
        this.error = e.response?.data?.message || '정보 수정에 실패했습니다.'
        // 차량번호 중복 오류일 경우 따로 표시
        if (e.response?.data?.message && e.response.data.message.includes('차량번호')) {
          this.carNumberError = e.response.data.message
        }
      } finally {
        this.loading = false
      }
    }
  },
  beforeUnmount() {
    if (this.authTimer) clearInterval(this.authTimer)
    if (this.resendTimer) clearInterval(this.resendTimer)
  }
}
</script>

<style scoped>
.card {
  border: none;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.form-label {
  color: #374151;
  font-weight: 500;
}

.form-control:disabled {
  background-color: #f5f5f5;
}
</style>
